/**
 * The deadline radar's words and colors (#2096), shared by the Command Center
 * tile and the toolbar Deadlines chip. Both read ONE server list
 * (`services/deadlineRadar.ts`): licenses, CE, city registrations, gauges,
 * insurance and driver's licenses due within 30 days or already out of date.
 */
export type DeadlineKind =
    | "tester_license"
    | "license"
    | "ce"
    | "city_registration"
    | "gauge"
    | "insurance"
    | "drivers_license";

export interface DeadlineRow {
    kind?: DeadlineKind;
    label: string;
    /** YYYY-MM-DD — the last good day. */
    due_on: string;
    /** Negative = out of date. */
    days_left?: number;
    href: string;
}

export interface DeadlinesView {
    windowDays: number;
    count: number;
    outOfDate: number;
    tone: "red" | "amber" | null;
    items: DeadlineRow[];
}

export const DEADLINES_KEY = ["command-center", "deadlines"] as const;

/** "11/14" this year, "11/14/2027" otherwise. */
export function shortDue(key: string, todayKey: string): string {
    const m = +key.slice(5, 7);
    const d = +key.slice(8, 10);
    return key.slice(0, 4) === todayKey.slice(0, 4) ? `${m}/${d}` : `${m}/${d}/${key.slice(0, 4)}`;
}

/** How long is left, in plain words. */
export function dueWords(daysLeft: number): string {
    if (daysLeft < 0) return "Out of date";
    if (daysLeft === 0) return "Today";
    if (daysLeft === 1) return "Tomorrow";
    return `${daysLeft} days`;
}

/** Red when out of date, amber inside the window. */
export function rowTone(daysLeft: number): "red" | "amber" {
    return daysLeft < 0 ? "red" : "amber";
}

/** Whole days from `todayKey` to `dueKey`, for rows that came without `days_left`. */
export function daysLeftOf(row: Pick<DeadlineRow, "due_on" | "days_left">, todayKey: string): number {
    if (typeof row.days_left === "number") return row.days_left;
    const a = Date.UTC(+todayKey.slice(0, 4), +todayKey.slice(5, 7) - 1, +todayKey.slice(8, 10));
    const k = row.due_on.slice(0, 10);
    const b = Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10));
    return Math.round((b - a) / 86_400_000);
}

/** The chip: hidden at zero; red when anything is out of date. */
export function deadlinesChipState(view: DeadlinesView | undefined): {
    show: boolean;
    tone: "red" | "amber";
    label: string;
} {
    const n = view?.count ?? 0;
    if (!view || n === 0) return { show: false, tone: "amber", label: "No deadlines" };
    return { show: true, tone: view.outOfDate > 0 ? "red" : "amber", label: `Deadlines · ${n}` };
}
